import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { StrataConfig } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export function useStrataConfig() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["strata-config", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("strata_config")
        .select("*")
        .eq("organization_id", activeOrgId!)
        .single();
      if (error) throw error;
      return data as StrataConfig;
    },
  });
}

export function useUpdateStrataConfig() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (updates: Partial<StrataConfig>) => {
      if (!activeOrgId) throw new Error("No active organization");

      const { data, error } = await supabase
        .from("strata_config")
        .update(updates)
        .eq("organization_id", activeOrgId)
        .select()
        .single();
      if (error) throw error;

      // Log the change
      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Config updated",
        entity: "Strata Config",
        detail: `Configuration updated: ${Object.keys(updates).join(", ")}`,
        user_name: user?.fullName ?? "Admin",
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["strata-config", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      toast({ title: "Configuration saved", description: "Strata configuration has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
